import {
  BudgetImage,
  CommunicationImage,
  DashboardImage,
  PerformanceImage,
  ProcurementImage,
  QualityAssuranceImage,
  DevelopersDashboardImage,
  DevelopersCommunicationImage,
  DevelopersProcurementImage,
  DevelopersQualityImage,
  DevelopersCostControlImage,
} from "assets/images";
import {
  AzaleaLogo,
  ColabsLogo,
  EnnovateLogo,
  InvictusLogo,
  PrexLogo,
} from "assets/svg";
import { Partner } from "./PartnerSection";
import { DeveloperIcon, LenderIcon, OwnerIcon } from "assets/icons";

export interface FaqItem {
  question: string;
  answer: string;
  cta?: { label: string; to: string };
}

const contractorFaq: FaqItem[] = [
  {
    question: "What do I need to set up a profile on Bankolé?",
    answer:
      "you can sign up with basic information about your company within 10 minutes. To get verified and invited to bid on projects, you will need to fill out details on your company profile which can take another 10 - 15 minutes.",
  },
  {
    question: "How does Bankolé select contractors to invite to bid on projects?",
    answer:
      "To increase your chances of being invited to bid on projects, ensure that your company profile is fully filled out. Also, you are only likely to be invited to bid on projects where your company is demonstrably active.",
  },
  {
    question: "How does Bankolé help me better manage construction projects?",
    answer:
      "The suite of features on Bankolé help to improve the efficiency of running your contracting business. You will be better able to see across the projects you are handling and will be able to keep a close eye on all the resources in your projects.",
  },
  {
    question:
      "What types of features does Bankolé construction management software application offer?",
    answer:
      "The range of features for construction management includes project scheduling, budgeting, procurement management, document management, and communication tools. Our solution provides schedule tracking, resource allocation, and quality control features.",
  },
  {
    question: "Can I use Bankolé on any device?",
    answer:
      "Bankolé is a web-based or cloud-based tool, which mean it can be accessed from any device with an internet connection. Native mobile apps for iOS and Android devices are coming soon.",
  },
  {
    question: "How does Bankolé help with budgeting?",
    answer:
      "Bankolé helps you with budgeting by providing tools to track your costs and expenses, as well as generate reports to help you identify any potential cost overruns. This ensures that your projects stay on budget and that any potential issues can be addressed before they become major problems.",
  },
  {
    question: "Can Bankolé help me with scheduling?",
    answer:
      "Yes, Bankolé helps you with scheduling by providing you tools to create and track project schedules. It also prompts you ahead of tasks and notifies you of delayed tasks. This can help you ensure that projects stay on track and that all team members are aware of their responsibilities.",
  },
  {
    question: "Does Bankolé help with document management?",
    answer:
      "Bankolé helps with document management by providing a central repository for all project-related documents. This can include contracts, plans, drawings, and other important documents, and can help ensure that all team members have access to the most up-to-date versions of these documents.",
  },
];


export const homeFaq: FaqItem[] = [
  {
    question: "What is Bankolé?",
    answer:
      "Bankolé is a digital platform designed to lower the risks involved in investing in real estate and infrastructure projects by enabling greater visibility and control of projects.",
  },
  {
    question: "Who can use this platform?",
    answer:
      "Bankolé is used by lenders to monitor projects in their lending portfolio, by developers to fund and manage their projects, and by organizations or individuals to effectively deliver their projects.",
  },
  {
    question: "How does it make projects safer for investors and lenders?",
    answer:
      "Bankolé provides smart tools that continually measure performance against plans; across time, budget and quality ensuring that project stakeholders are able to address issues before they become problems.",
  },
  {
    question: "Is it only for projects in Africa?",
    answer:
      "While Bankolé has been designed to deal with the peculiar project management conventions in African markets, it offers a robust suite of tools that can be used to manage projects anywhere in the world.",
  },
  {
    question: "Can I use it for ongoing projects or only for new ones?",
    answer:
      "You can bring both new and existing projects onto Bankolé, so you get the benefits of better tracking and transparency straight away.",
  },
  {
    question: "How does it improve transparency and accountability?",
    answer:
      "Every bit of project activity, spending, and communication is recorded and easily accessible to those with permission, so everyone stays in the loop with up-to-date information.",
  },
  {
    question: "What types of projects can be managed?",
    answer:
      "From residential and commercial builds to infrastructure projects big and small, Bankolé helps you stay organised and on track. More importantly, you can also manage multiple projects in one cluster or manage them as a portfolio.",
  },
  {
    question: "Is my data secure and private?",
    answer:
      "Your data’s safety is a top priority. Bankolé uses industry-standard encryption and strict access controls to keep everything confidential and secure.",
  },
  {
    question: "What does it cost to use Bankolé?",
    answer: "We have a range of pricing options to suit every user.",
    cta: { label: "See Pricing page", to: "/pricing/premium" },

  },
];

export const lenderFaq: FaqItem[] = [
  {
    question:
      "How does the platform help me monitor my lending portfolio in real time?",
    answer:
      "Receive live updates on all your funded projects, including progress, expenses, and milestones, so you can make informed decisions instantly.",
  },
  {
    question:
      "Can I track borrower progress and use it to trigger disbursements?",
    answer:
      "Yes. Funding releases can be linked to specific project milestones, with compliance monitored automatically for added control and security.",
  },
  {
    question: "How does it reduce project risk and protect my capital?",
    answer:
      "The platform provides full visibility and timely alerts for delays or budget overruns, allowing you to intervene early and safeguard your investments.",
  },
  {
    question: "Will I get alerts for delays, cost overruns, or scope changes?",
    answer:
      "Yes. Notifications keep you informed of any project deviations, so you can respond proactively.",
  },
  {
    question: "Can I see performance across multiple projects?",
    answer:
      "Yes, the cluster dashboard provides you a composite view of progress, budgets, and risk levels across all your projects.",
  },
  {
    question: "Does the system store all key project documents securely?",
    answer:
      "All contracts, plans, and reports are safely stored and easily accessible to authorized users, ensuring document security and convenience.",
  },
  {
    question: "Can I use it for ongoing projects or only for new ones?",
    answer:
      "You can onboard new and current projects. For ongoing projects, you will simply upload historical information and just continue without hitch.",
  },
  {
    question: "Can my team and external partners access the same data?",
    answer:
      "Yes. Role-based permissions allow teams, partners, and auditors to access shared data, enhancing collaboration while maintaining security.",
  },
  {
    question: "How easy is it for my borrowers to adopt the system?",
    answer:
      "The platform is designed for easy use and includes onboarding support, making adoption smooth for all borrowers.",
  },
  {
    question: "Does it work for both real estate and infrastructure projects?",
    answer:
      "Yes. The system is built to accommodate a wide range of project types and scales—whether real estate, infrastructure, or beyond.",
  },
  {
    question:
      "Can Bankolé provide project monitoring and data management service?",
    answer:
      "Yes, in addition to software, Bankolé offers comprehensive project management involving the deployment of professionals for normal supervision of your project.",
  },
];

export const developerFaq: FaqItem[] = [
  {
    question:
      "Can using this platform improve my chances of getting project financing?",
    answer:
      "Yes, Bankole provides project financing to qualified developers. Using this platform also increases your chances of receiving project finances from other lenders or investors due to the high level efficiency and transparency that it brings to your projects.",
  },
  {
    question: "How does it help me prove progress and credibility to lenders?",
    answer:
      "The platform provides transparent reporting and milestone tracking to demonstrate project performance.",
  },
  {
    question: "Can I manage contractors and timelines?",
    answer:
      "Yes, the platform includes tools for task management, scheduling, and vendor tracking.",
  },
  {
    question: "Does it help me keep track of costs and budgets in real time?",
    answer:
      "Absolutely. You can monitor expenses and compare them against your planned budget at any time.",
  },
  {
    question: "Will my project team need training to use it?",
    answer:
      "The platform is intuitive, but training and onboarding support are available.",
  },
  {
    question: "Can it be used for multiple projects at once?",
    answer: "Yes, you can manage multiple projects from a single dashboard.",
  },
  {
    question: "How does it help prevent delays and disputes?",
    answer:
      "Real-time tracking, alerts, and clear documentation reduce misunderstandings and keep the project on schedule.",
  },
  {
    question: "What’s the setup time before I can start managing my project?",
    answer:
      "Setup is quick; you can start tracking your first project within minutes of signing up.",
  },
];


export const ownerFaq: FaqItem[] = [
  {
    question:
      "How does the platform help me ensure my project is delivered on time and on budget?",
    answer:
      "By monitoring milestones, costs, and timelines in real-time, you can take corrective action before issues escalate.",
  },
  {
    question: "Can I see exactly where my money is going at each stage?",
    answer:
      "Yes, the platform provides transparent financial tracking down to individual expenses.",
  },
  {
    question: "Does it help me choose and manage contractors?",
    answer:
      "Yes, you can select from a pool of vetted contractors and after engagement, you can track contractor performance, schedule tasks, and store contracts securely.",
  },
  {
    question:
      "Can I use it even if I’m not physically close to the project site?",
    answer:
      "Absolutely. The platform is cloud-based and accessible from anywhere. It is designed to power remote project management in particular.",
  },
  {
    question:
      "Does it provide progress reports I can understand without technical training?",
    answer:
      "Yes, reports are presented in clear, easy-to-understand dashboards and summaries.",
  },
  {
    question:
      "Can family members, partners, or trustees also access project updates?",
    answer: "Yes, you can grant controlled access to other stakeholders.",
  },
  {
    question: "Is it suitable for both residential and commercial projects?",
    answer:
      "Yes, the platform supports all types of real estate and infrastructure projects.",
  },
  {
    question:
      "Can Bankolé provide project monitoring and data management service?",
    answer:
      "Yes, in addition to software, Bankolé offers comprehensive project management involving the deployment of professionals for normal supervision of your project.",
  },
];

const enterpriseFaq: FaqItem[] = [
  {
    question: "What do I need to onboard my projects on Bnkle?",
    answer:
      "You can sign up and create an account on Bnklé within 5 minutes. Onboarding projects will depend on whether you are onboarding an individual project or a cluster of projects. Individual projects can be created in less than 5 minutes, while clusters will depend on how many projects are within a cluster.",
  },
  {
    question:
      "Can I onboard my team and contractors who work for my company on Bnkle.",
    answer:
      "Yes, Bnklé allows you to onboard everyone connected to your project in different roles on the platform. Onboarding is done simply by sending out invites to their email with a few clicks.",
  },
  {
    question: "How does Bnklé help me better manage construction projects?",
    answer:
      "The suite of features on Bnklé help to improve the efficiency of running your real estate development business. The Bnklé data rich dashboard allows you to see across the projects you are handling, and so keep a close eye on how your entire portfolio is developing while having access to granular details about the resources your projects are using.",
  },
  {
    question:
      "What types of features does Bnklé construction management software application offer?",
    answer:
      "The range of features for construction management includes project scheduling, budgeting, procurement management, document management, and communication tools. Our solution provides schedule tracking, resource allocation, and quality control features.",
  },
  {
    question: "Can I use Bnklé on any device?",
    answer:
      "Bnklé is a web-based or cloud-based tool, which mean it can be accessed from any device with an internet connection. Native mobile apps for iOS and Android devices are coming soon.",
  },
  {
    question: "How does Bnklé help with budgeting?",
    answer:
      "Bnklé helps you with budgeting by providing tools to track your costs and expenses, as well as generate reports to help you identify any potential cost overruns. This ensures that your projects stay on budget and that any potential issues can be addressed before they become major problems.",
  },
  {
    question: "Can Bnklé help me with scheduling?",
    answer:
      "Yes, Bnklé helps you with scheduling by providing you tools to create and track project schedules. It also prompts you ahead of tasks and notifies you of delayed tasks. This can help you ensure that projects stay on track and that all team members are aware of their responsibilities.",
  },
  {
    question: "Does Bnklé help with document management?",
    answer:
      "Bnklé helps with document management by providing a central repository for all project-related documents. This can include contracts, plans, drawings, and other important documents, and can help ensure that all team members have access to the most up-to-date versions of these documents.",
  },
];

let x = {
  question: "",
  answer: "",
};

const testimonials = [
  {
    message:
      "Bankolé is a wonderful project management tool. It has enabled me monitor my project remotely. From the negotiations to the actual site implementation with visual updates. It also provides a seamless interface for all the actors on the project.",
    name: "Samson Opaluwah. PhD.",
    title: "Chairman, Council of Registered Builders of Nigeria (CORBON)",
  },
  {
    message:
      "The Bankolé platform was especially important as a central point to constantly communicate the implementation of the project, allowing us to see pictures of work progress, view/review invoices, and monitor spending, among others. This level of communication, transparency, and coordination was especially crucial as I was overseas during the start of the project. Needless to say, the result is nothing short of impressive.",
    name: "Bukky Shonibare",
    title: "Executive Director, Invictus Africa",
  },
];

export interface Feature {
  name: string;
  title?: string;
  image: string;
  description: string;
}

const features: Feature[] = [
  {
    name: "Dashboard",
    image: DashboardImage,
    description:
      "Deploy capital to build projects, with tools that lower risk, increase efficiency, enhance oversight, and ensure stakeholder accountability.",
  },
  {
    name: "Performance Tracking",
    image: PerformanceImage,
    description:
      "Smart charts to track project plans to ensure timely delivery",
  },
  {
    name: "Budget Control",
    image: BudgetImage,
    description:
      "Follow the money! Gain complete visibility and control of project finance.",
  },
  {
    name: "Procurement Management",
    image: ProcurementImage,
    description:
      "Get granular details of material procurement and consumption on projects.",
  },
  {
    name: "Communication",
    image: CommunicationImage,
    description:
      "Keep every stakeholder in the loop with real time communication and reporting.",
  },
  {
    name: "Quality Assurance",
    image: QualityAssuranceImage,
    description:
      "Ensure every task is delivered to specification with our robust quality assurance checklist and approval system.",
  },
];

const developerFeatures: Feature[] = [
  {
    name: "Dashboard",
    image: DevelopersDashboardImage,
    description:
      "Deploy capital to build projects, with tools that lower risk, increase efficiency, enhance oversight, and ensure stakeholder accountability.",
  },
  {
    name: "Cost Control",
    image: DevelopersCostControlImage,
    description:
      "Use our budgeting and book keeping tool to track every kobo and match expenditure to progress",
  },
  {
    name: "Procurement Management",
    image: DevelopersProcurementImage,
    description:
      "Get granular details of material procurement and consumption on projects.",
  },
  {
    name: "Quality Management",
    image: DevelopersQualityImage,
    description:
      "Our robust check-listing feature ensures that all tasks are delivered to the right quality specification.",
  },
  {
    name: "Communication",
    image: DevelopersCommunicationImage,
    description:
      "keep every stakeholder in the loop with real time communication and reporting.",
  },
];

const personas = [
  {
    title: "Lenders",
    description: "Gain effective oversight of the projects you finance.",
    icon: LenderIcon,
    link: "lender",
  },
  {
    title: "Property Developers",
    description: "Access affordable finance for your projects.",
    icon: DeveloperIcon,
    link: "developer",
  },
  {
    title: "Project Owners",
    description: "Get end-to-end project delivery",
    icon: OwnerIcon,
    link: "owner",
  },
];

export const partners: Partner[] = [
  { src: InvictusLogo, alt: "Invictus" },
  { src: ColabsLogo, alt: "CoLabs" },
  { src: PrexLogo, alt: "Prex", className: "xl:w-[124px] xl:h-[36px]" },
  { src: EnnovateLogo, alt: "Ennovate Lab" },
  { src: AzaleaLogo, alt: "Azalea", className: "object-contain" },
];

export {
  testimonials,
  contractorFaq,
  enterpriseFaq,
  features,
  developerFeatures,
  personas,
};